/**
 * Risk ▸ Library metinleri (K3; ADR 0087): şirket RA kütüphanesi, yedi sekmeli Builder, iş akışı
 * (Draft → Submitted → Under Review → Returned / Approved → Published), yayımlı sürüm ve gemi
 * ataması. Adlar ortağın belgelerindeki gibi İngilizce (FD §5, §12); Türkçe ikinci dil.
 */

export const riskLibraryEn = {
  title: 'Risk Assessment Library',
  description:
    'Company risk assessments: written in the Builder, reviewed, approved and published as fixed versions. Vessels use the published version assigned to them.',
  shipTitle: 'Company Risk Library',
  shipDescription:
    'Published company risk assessments. A vessel task-specific risk assessment starts from a current version assigned to this vessel.',
  shipNote:
    'The company writes and publishes these assessments; on board they are read only. A version that is not assigned to this vessel cannot be used as current.',
  stats: {
    published: 'Published',
    publishedNote: '{{count}} migrated from templates',
    workflow: 'In Workflow',
    workflowNote: '{{review}} awaiting review',
    drafts: 'Drafts',
    draftsNote: 'rows without residual risk: {{missing}}',
    assigned: 'Assigned',
    assignedNote: 'of {{count}} published',
    current: 'Current on Board',
    currentNote: '{{count}} not assigned',
  },
  search: 'Search by ID, title or activity',
  filter: {
    status: 'Status',
    allStatuses: 'All statuses',
  },
  columns: {
    code: 'Assessment ID',
    title: 'Title',
    revision: 'Revision',
    published: 'Published',
    status: 'Status',
    rows: 'Rows',
    inherent: 'Inherent Risk',
    residual: 'Target Residual',
    acceptance: 'Acceptance',
    assigned: 'Assigned To',
    review: 'Review',
    availability: 'On Board',
  },
  tableLabel: 'Company risk assessments',
  showing: 'Showing {{from}} to {{to}} of {{total}} assessments',
  pagination: 'Library pages',
  open: 'Open {{code}}',
  missingResidual: '{{count}} missing',
  none: '—',
  statuses: {
    draft: 'Draft',
    submitted: 'Submitted',
    under_review: 'Under Review',
    returned: 'Returned',
    approved: 'Approved',
    published: 'Published',
  },
  availability: {
    current: 'Current',
    not_assigned: 'Not assigned',
    superseded: 'Superseded',
  },
  migrated: 'Migrated',
  empty: {
    title: 'No company risk assessments yet',
    office:
      'Start an assessment from an activity of the master data, or create drafts from the imported risk rows.',
    ship: 'The company has not published a risk assessment yet.',
    noMatch: 'No assessment matches the search.',
    error: 'The risk assessment library could not be loaded.',
  },
  actions: {
    new: 'New Assessment',
    drafts: 'Create Drafts from Risk Rows',
    draftsDone: '{{count}} draft assessments created; {{skipped}} already existed.',
    back: 'Back to the library',
    save: 'Save Draft',
    discardChanges: 'Discard Changes',
    saved: 'Draft saved.',
    submit: 'Submit for Review',
    startReview: 'Start Review',
    return: 'Return',
    accept: 'Accept as {{role}}',
    approve: 'Approve',
    publish: 'Publish Revision {{revision}}',
    revise: 'New Revision',
    done: {
      submitted: 'Submitted for review.',
      review_started: 'Review started.',
      returned: 'Returned to the author.',
      accepted: 'Escalated risk accepted.',
      approved: 'Approved. It can be published now.',
      published: 'Revision {{revision}} published. Assigned vessels receive it with the next sync.',
      revised: 'Revision {{revision}} opened as a draft. Revision {{published}} stays in force.',
      created: '{{code}} created as a draft.',
    },
  },
  newDialog: {
    title: 'New company risk assessment',
    activity: 'Activity',
    help: 'The assessment ID comes from the activity code and does not change between revisions. Rows are taken from the activity risk rows.',
    placeholder: 'Pick an activity',
    none: 'Every activity already has an assessment.',
    create: 'Create Draft',
  },
  dialog: {
    submitTitle: 'Submit for review',
    submitBody:
      'Revision {{revision}} goes to review as shown. It cannot be changed until it is returned.',
    reviewTitle: 'Start review',
    reviewBody: 'You start the review of revision {{revision}}.',
    returnTitle: 'Return to the author',
    returnBody: 'Write what must be corrected.',
    acceptTitle: 'Accept the escalated risk',
    acceptBody:
      'You accept, as {{role}}, the residual risk of the rows marked "Escalation required" in revision {{revision}}.',
    approveTitle: 'Approve revision {{revision}}',
    approveBody: 'You approve revision {{revision}} as shown.',
    approveSelf:
      'You submitted this revision. Approving it yourself is allowed and is recorded in the history.',
    publishTitle: 'Publish revision {{revision}}',
    publishBody:
      'The revision becomes a fixed version with the current risk matrix. Later changes need a new revision; records already made keep this version.',
    reviseTitle: 'Open a new revision',
    reviseBody:
      'Revision {{next}} opens as a draft with the content of revision {{published}}. Revision {{published}} stays in force until the new one is published.',
    confirm: 'Confirm',
  },
  header: {
    revision: 'Revision {{revision}}',
    published: 'Published {{revision}}',
    noPublished: 'Not published yet',
    activity: 'Activity {{code}}',
    readOnly: 'Read only',
    readOnlyVersion: 'Published version {{revision}} · read only',
  },
  tabs: {
    general: 'General',
    rows: 'Hazards',
    initial: 'Initial Risk',
    existing: 'Existing Controls',
    additional: 'Additional Controls',
    residual: 'Residual Risk',
    publication: 'Approval & Publication',
  },
  general: {
    id: 'Assessment ID',
    revision: 'Revision',
    title: 'Title',
    activity: 'Activity',
    noActivity: 'Migrated from a template; no activity',
    type: 'Assessment Type',
    vesselGroups: 'Vessel Types',
    location: 'Location / Context',
    team: 'Assessment Team',
    procedures: 'Procedures',
    preparedDay: 'Prepared',
    reviewDay: 'Review Due',
    scope: 'Scope',
    assumptions: 'Assumptions',
    exclusions: 'Exclusions',
  },
  checks: {
    title: 'Operational Checklist — Activity-Specific Checks',
    note: 'On board the verification asks the six common checks, these activity-specific checks and every existing control marked mandatory ({{controls}} in this assessment). Activity-specific checks are optional.',
    empty: 'No activity-specific checks.',
    add: 'Add Check',
    text: 'Check {{n}}',
    naAllowed: 'N/A allowed (with reason)',
    remove: 'Remove check {{n}}',
  },
  rows: {
    note: 'Each row is one step, one hazard and its consequences. An activity may have several hazards; the same hazard may appear in several steps.',
    step: 'Task Step',
    hazard: 'Hazard',
    consequence: 'Consequences',
    exposed: 'Exposed',
    library: 'Library',
    requested: 'New hazard requested',
    free: 'Free text',
    add: 'Add Row',
    duplicate: 'Duplicate row {{n}}',
    remove: 'Delete draft row {{n}}',
    link: 'Link Existing Hazard',
    linkPlaceholder: 'Pick a library hazard',
    request: 'Request New Hazard',
    requestNote:
      'The hazard is not in the master data yet. Add it in Master Data and link it before publishing.',
    empty: 'No rows yet. Add a row or link a hazard from the library.',
    rowTitle: 'Row {{n}} · {{step}} · {{hazard}}',
  },
  initial: {
    note: 'Select likelihood and severity; the score is calculated and cannot be typed. Write why.',
    likelihood: 'Likelihood',
    severity: 'Severity',
    score: 'Inherent Risk',
    rationale: 'Rationale',
    assumptions: 'Assumptions',
    highNote: 'High or Critical inherent risk',
  },
  existing: {
    note: 'Selecting a control does not mean it is in place. Mark it in place only when it is verified.',
    measure: 'Control',
    hierarchy: 'Hierarchy',
    requirement: 'Requirement',
    procedure: 'Procedure',
    owner: 'Owner',
    evidence: 'Evidence',
    inPlace: 'In Place',
    effectiveness: 'Effectiveness Note',
    add: 'Add Existing Control',
    remove: 'Remove control {{n}}',
    current: 'Score current risk (with verified controls)',
    currentScore: 'Current Risk',
    none: 'No existing control.',
  },
  inPlace: { yes: 'Yes', no: 'No', unknown: 'Not verified' },
  additional: {
    note: 'Required when the risk is above the acceptance threshold. A draft control does not lower the risk; the target residual risk stays an estimate until the controls are verified.',
    measure: 'Control',
    hierarchy: 'Hierarchy',
    effect: 'Expected Effect',
    effectRationale: 'Effect Rationale',
    responsible: 'Responsible Role',
    due: 'Due',
    evidence: 'Evidence',
    verifier: 'Verified By',
    criteria: 'Completion Criteria',
    status: 'Status',
    add: 'Add Additional Control',
    remove: 'Remove control {{n}}',
    none: 'No additional control.',
  },
  effects: { likelihood: 'Likelihood', severity: 'Severity', both: 'Both' },
  additionalStatuses: {
    draft: 'Draft (proposal)',
    planned: 'Planned',
    in_progress: 'In Progress',
    implemented: 'Implemented',
    verified: 'Verified',
  },
  residual: {
    note: 'Target residual risk after the additional controls. The acceptance status comes from the company risk matrix.',
    likelihood: 'Likelihood',
    severity: 'Severity',
    notSet: 'Not set',
    rationale: 'Rationale',
    dependency: 'Dependency',
    scores: 'Inherent · Current · Target',
    acceptance: 'Acceptance',
    estimate: 'Estimate until verified',
    verified: 'Verified',
    acceptedBy: 'Accepted by {{roles}}',
  },
  publication: {
    workflow: 'Workflow',
    issues: 'Before submitting',
    issuesNone: 'Ready: the content passes the publication checks.',
    acceptors: 'Escalation acceptance',
    acceptorsNone: 'No row needs escalation acceptance in the office.',
    acceptorsVessel: 'On board, {{roles}} accepts before the operation (verification).',
    accepted: 'Accepted',
    pending: 'Pending',
    history: 'History',
    historyNone: 'No workflow step yet.',
    sameActor: 'same person as the submitter',
    versions: 'Published Versions',
    versionsNone: 'No published version yet.',
    version: 'Revision {{revision}} · {{at}} · {{by}}',
    migratedVersion: 'Revision {{revision}} · migrated from a template · {{at}}',
    matrix: 'Risk matrix v{{version}}',
    legacy: 'Legacy 5-band',
    assignments: 'Assigned Vessels',
    assignmentsNote:
      'A vessel uses the latest published version only when it is assigned by vessel type or by name.',
    assignmentsNone: 'Not assigned: no vessel can use it as current.',
    assignGroup: 'Vessel type',
    assignVessel: 'Vessel',
    assign: 'Assign',
    unassign: 'Remove assignment {{target}}',
    groupLabel: 'All {{group}}',
  },
  events: {
    submitted: 'Submitted',
    review_started: 'Review started',
    returned: 'Returned',
    accepted: 'Escalation accepted ({{role}})',
    approved: 'Approved',
    published: 'Published',
  },
  issues: {
    ROWS_REQUIRED: 'Add at least one row.',
    PREPARED_DAY_REQUIRED: 'Enter the prepared date (General).',
    REVIEW_DAY_REQUIRED: 'Enter the review due date (General).',
    REVIEW_BEFORE_PREPARED: 'The review date must be after the prepared date.',
    REVIEW_TOO_LATE: 'The review is due at most 12 months after the prepared date.',
    HAZARD_REQUESTED: 'Row {{n}}: link the requested hazard from the master data.',
    CURRENT_ABOVE_INITIAL: 'Row {{n}}: the current risk cannot be above the inherent risk.',
    CURRENT_WITHOUT_CONTROLS:
      'Row {{n}}: the current risk is lower only with an existing control verified in place.',
    ADDITIONAL_CONTROLS_REQUIRED:
      'Row {{n}}: the risk is above the acceptance threshold; add an additional control that is not a draft.',
    ADDITIONAL_CONTROL_INCOMPLETE:
      'Row {{n}}: give each additional control an expected effect and a responsible role.',
    RESIDUAL_REQUIRED: 'Row {{n}}: enter the target residual risk.',
    RESIDUAL_ABOVE_INITIAL: 'Row {{n}}: the residual risk cannot be above the initial risk.',
    RESIDUAL_REDUCTION_UNSUPPORTED:
      'Row {{n}}: a draft control does not lower the risk; plan the additional control first.',
    SEVERITY_REDUCTION_UNSUPPORTED:
      'Row {{n}}: only a control other than PPE, expected to reduce severity, lowers the severity.',
    RESIDUAL_RATIONALE_REQUIRED: 'Row {{n}}: write why the residual risk is lower.',
    RESIDUAL_UNACCEPTABLE:
      'Row {{n}}: the residual risk is unacceptable; it cannot be published or used.',
  },
  errors: {
    GENERIC: 'The action failed.',
    FORBIDDEN: 'You are not authorised for this action.',
    NOT_FOUND: 'The assessment was not found.',
    VALIDATION_FAILED: 'Some fields are not valid.',
    INVALID_RA_LIBRARY: 'Some fields are not valid: {{detail}}',
    ROW_VERSION_CONFLICT: 'Someone else changed this assessment. Reload and try again.',
    CONTENT_HASH_MISMATCH: 'The assessment changed meanwhile. Reload and try again.',
    REVISION_LOCKED: 'The revision is in review and cannot be changed.',
    INVALID_TRANSITION: 'This step is not possible in the current status.',
    STALE_DECISION: 'The assessment changed meanwhile. Reload and try again.',
    REASON_REQUIRED: 'Write a reason of at least 5 characters.',
    ACCEPTANCE_REQUIRED: 'The escalated risk must be accepted first ({{detail}}).',
    ACCEPTANCE_NOT_REQUIRED: 'No escalation acceptance is needed from this role.',
    ACCEPTOR_ROLE_REQUIRED: 'Only a person with the role can accept: {{detail}}.',
    ALREADY_ACCEPTED: 'The risk is already accepted for this role.',
    NOT_APPROVED: 'The revision must be approved first.',
    MATRIX_VERSION_MISMATCH: 'The risk matrix changed. Reload and approve again.',
    TEMPLATE_NOT_APPROVED:
      'The activity {{detail}} has not passed the shore technical review: it cannot be published or assigned to vessels yet (Master Data ▸ Activities).',
    LINK_NOT_ASSESSED:
      'A risk row of the activity has no initial likelihood and severity yet ({{detail}}). Assess it in Master Data first.',
    DUPLICATE_CODE: 'The activity already has a risk assessment.',
    DUPLICATE_ASSIGNMENT: 'The assessment is already assigned there.',
    NOT_PUBLISHED: 'Publish the assessment first.',
    ACTIVITY_RETIRED: 'The activity is retired.',
    REAUTHENTICATION_REQUIRED: 'Enter your password to confirm.',
    INVALID_CREDENTIALS: 'The password is wrong.',
  },
};

export const riskLibraryTr: typeof riskLibraryEn = {
  title: 'Risk Değerlendirmesi Kütüphanesi',
  description:
    "Şirketin risk değerlendirmeleri: Builder'da yazılır, incelenir, onaylanır ve sabit sürüm olarak yayımlanır. Gemiler kendilerine atanmış yayımlı sürümü kullanır.",
  shipTitle: 'Şirket Risk Kütüphanesi',
  shipDescription:
    'Şirketin yayımladığı risk değerlendirmeleri. Gemiye özel değerlendirme, bu gemiye atanmış güncel bir sürümden başlar.',
  shipNote:
    'Bu değerlendirmeleri şirket yazar ve yayımlar; gemide salt okunurdur. Bu gemiye atanmamış sürüm güncel diye kullanılamaz.',
  stats: {
    published: 'Yayımlı',
    publishedNote: '{{count}} şablondan dönüştü',
    workflow: 'İş Akışında',
    workflowNote: '{{review}} inceleme bekliyor',
    drafts: 'Taslak',
    draftsNote: 'kalan riski boş satır: {{missing}}',
    assigned: 'Atanmış',
    assignedNote: '{{count}} yayımlıdan',
    current: 'Gemide Güncel',
    currentNote: '{{count}} atanmamış',
  },
  search: 'Kimlik, başlık ya da faaliyetle ara',
  filter: {
    status: 'Durum',
    allStatuses: 'Bütün durumlar',
  },
  columns: {
    code: 'Değerlendirme No',
    title: 'Başlık',
    revision: 'Revizyon',
    published: 'Yayımlı',
    status: 'Durum',
    rows: 'Satır',
    inherent: 'İlk Risk',
    residual: 'Hedef Kalan Risk',
    acceptance: 'Kabul',
    assigned: 'Atanan',
    review: 'Gözden Geçirme',
    availability: 'Gemide',
  },
  tableLabel: 'Şirketin risk değerlendirmeleri',
  showing: '{{total}} değerlendirmeden {{from}}–{{to}}',
  pagination: 'Kütüphane sayfaları',
  open: '{{code}} aç',
  missingResidual: '{{count}} eksik',
  none: '—',
  statuses: {
    draft: 'Taslak',
    submitted: 'Gönderildi',
    under_review: 'İncelemede',
    returned: 'İade Edildi',
    approved: 'Onaylandı',
    published: 'Yayımlandı',
  },
  availability: {
    current: 'Güncel',
    not_assigned: 'Atanmamış',
    superseded: 'Eski sürüm',
  },
  migrated: 'Şablondan',
  empty: {
    title: 'Henüz şirket risk değerlendirmesi yok',
    office:
      'Ana verideki bir faaliyetten değerlendirme başlatın ya da içe aktarılmış risk satırlarından taslak oluşturun.',
    ship: 'Şirket henüz risk değerlendirmesi yayımlamadı.',
    noMatch: 'Aramaya uyan değerlendirme yok.',
    error: 'Risk değerlendirmesi kütüphanesi yüklenemedi.',
  },
  actions: {
    new: 'Yeni Değerlendirme',
    drafts: 'Risk Satırlarından Taslak Oluştur',
    draftsDone: '{{count}} taslak değerlendirme oluşturuldu; {{skipped}} zaten vardı.',
    back: 'Kütüphaneye dön',
    save: 'Taslağı Kaydet',
    discardChanges: 'Değişiklikleri Bırak',
    saved: 'Taslak kaydedildi.',
    submit: 'İncelemeye Gönder',
    startReview: 'İncelemeyi Başlat',
    return: 'İade Et',
    accept: '{{role}} olarak kabul et',
    approve: 'Onayla',
    publish: 'Revizyon {{revision}} Yayımla',
    revise: 'Yeni Revizyon',
    done: {
      submitted: 'İncelemeye gönderildi.',
      review_started: 'İnceleme başladı.',
      returned: 'Yazara iade edildi.',
      accepted: 'Yükseltilen risk kabul edildi.',
      approved: 'Onaylandı. Artık yayımlanabilir.',
      published: 'Revizyon {{revision}} yayımlandı. Atanmış gemilere bir sonraki senkronla iner.',
      revised:
        'Revizyon {{revision}} taslak olarak açıldı. Revizyon {{published}} yürürlükte kalır.',
      created: '{{code}} taslak olarak oluşturuldu.',
    },
  },
  newDialog: {
    title: 'Yeni şirket risk değerlendirmesi',
    activity: 'Faaliyet',
    help: 'Değerlendirme numarası faaliyet kodundan gelir ve revizyonlar boyunca değişmez. Satırlar faaliyetin risk satırlarından alınır.',
    placeholder: 'Faaliyet seçin',
    none: 'Her faaliyetin zaten değerlendirmesi var.',
    create: 'Taslak Oluştur',
  },
  dialog: {
    submitTitle: 'İncelemeye gönder',
    submitBody:
      'Revizyon {{revision}} gösterildiği gibi incelemeye gider. İade edilene kadar değiştirilemez.',
    reviewTitle: 'İncelemeyi başlat',
    reviewBody: 'Revizyon {{revision}} incelemesini başlatıyorsunuz.',
    returnTitle: 'Yazara iade et',
    returnBody: 'Düzeltilmesi gerekeni yazın.',
    acceptTitle: 'Yükseltilen riski kabul et',
    acceptBody:
      'Revizyon {{revision}} içinde "Yükseltme gerekir" işaretli satırların kalan riskini {{role}} olarak kabul ediyorsunuz.',
    approveTitle: 'Revizyon {{revision}} onayı',
    approveBody: 'Revizyon {{revision}} gösterildiği gibi onaylıyorsunuz.',
    approveSelf:
      'Bu revizyonu siz gönderdiniz. Kendi onayınıza izin verilir ve geçmişte işaretlenir.',
    publishTitle: 'Revizyon {{revision}} yayımla',
    publishBody:
      'Revizyon geçerli risk matrisiyle sabit bir sürüm olur. Sonraki değişiklik yeni revizyon ister; yapılmış kayıtlar bu sürümü taşır.',
    reviseTitle: 'Yeni revizyon aç',
    reviseBody:
      'Revizyon {{next}}, revizyon {{published}} içeriğiyle taslak olarak açılır. Yenisi yayımlanana dek revizyon {{published}} yürürlükte kalır.',
    confirm: 'Onayla',
  },
  header: {
    revision: 'Revizyon {{revision}}',
    published: 'Yayımlı {{revision}}',
    noPublished: 'Henüz yayımlanmadı',
    activity: 'Faaliyet {{code}}',
    readOnly: 'Salt okunur',
    readOnlyVersion: 'Yayımlı sürüm {{revision}} · salt okunur',
  },
  tabs: {
    general: 'Genel',
    rows: 'Tehlikeler',
    initial: 'İlk Risk',
    existing: 'Mevcut Önlemler',
    additional: 'Ek Önlemler',
    residual: 'Kalan Risk',
    publication: 'Onay ve Yayım',
  },
  general: {
    id: 'Değerlendirme No',
    revision: 'Revizyon',
    title: 'Başlık',
    activity: 'Faaliyet',
    noActivity: 'Şablondan dönüştü; faaliyet yok',
    type: 'Değerlendirme Türü',
    vesselGroups: 'Gemi Tipleri',
    location: 'Yer / Bağlam',
    team: 'Değerlendirme Ekibi',
    procedures: 'Prosedürler',
    preparedDay: 'Hazırlama',
    reviewDay: 'Gözden Geçirme',
    scope: 'Kapsam',
    assumptions: 'Varsayımlar',
    exclusions: 'Hariç Tutulanlar',
  },
  checks: {
    title: 'Operasyon Kontrol Listesi — Faaliyete Özel Maddeler',
    note: 'Gemideki doğrulama altı ortak maddeyi, bu faaliyete özel maddeleri ve "zorunlu" işaretli her mevcut kontrolü sorar (bu değerlendirmede {{controls}}). Faaliyete özel maddeler isteğe bağlıdır.',
    empty: 'Faaliyete özel madde yok.',
    add: 'Madde Ekle',
    text: 'Madde {{n}}',
    naAllowed: 'N/A verilebilir (gerekçeyle)',
    remove: '{{n}}. maddeyi kaldır',
  },
  rows: {
    note: 'Her satır bir adım, bir tehlike ve sonuçlarıdır. Bir faaliyetin birden çok tehlikesi olabilir; aynı tehlike birden çok adımda görünebilir.',
    step: 'İş Adımı',
    hazard: 'Tehlike',
    consequence: 'Sonuçlar',
    exposed: 'Maruz Kalan',
    library: 'Kütüphane',
    requested: 'Yeni tehlike istendi',
    free: 'Serbest metin',
    add: 'Satır Ekle',
    duplicate: '{{n}}. satırı çoğalt',
    remove: '{{n}}. taslak satırı sil',
    link: 'Var Olan Tehlikeyi Bağla',
    linkPlaceholder: 'Kütüphaneden tehlike seçin',
    request: 'Yeni Tehlike İste',
    requestNote: "Tehlike henüz ana veride yok. Yayımdan önce Ana Veriler'de ekleyip bağlayın.",
    empty: 'Henüz satır yok. Satır ekleyin ya da kütüphaneden tehlike bağlayın.',
    rowTitle: 'Satır {{n}} · {{step}} · {{hazard}}',
  },
  initial: {
    note: 'Olasılık ve şiddeti seçin; skor hesaplanır, elle yazılmaz. Gerekçeyi yazın.',
    likelihood: 'Olasılık',
    severity: 'Şiddet',
    score: 'İlk Risk',
    rationale: 'Gerekçe',
    assumptions: 'Varsayımlar',
    highNote: 'High ya da Critical ilk risk',
  },
  existing: {
    note: 'Önlemi seçmek, uygulandığı anlamına gelmez. Yalnız doğrulandığında "yerinde" işaretleyin.',
    measure: 'Önlem',
    hierarchy: 'Hiyerarşi',
    requirement: 'Zorunluluk',
    procedure: 'Prosedür',
    owner: 'Sahibi',
    evidence: 'Kanıt',
    inPlace: 'Yerinde',
    effectiveness: 'Etkinlik Notu',
    add: 'Mevcut Önlem Ekle',
    remove: '{{n}}. önlemi kaldır',
    current: 'Güncel riski puanla (doğrulanmış önlemlerle)',
    currentScore: 'Güncel Risk',
    none: 'Mevcut önlem yok.',
  },
  inPlace: { yes: 'Evet', no: 'Hayır', unknown: 'Doğrulanmadı' },
  additional: {
    note: 'Risk kabul eşiğinin üstündeyse zorunludur. Taslak önlem riski düşürmez; önlemler doğrulanana dek hedef kalan risk bir tahmindir.',
    measure: 'Önlem',
    hierarchy: 'Hiyerarşi',
    effect: 'Beklenen Etki',
    effectRationale: 'Etki Gerekçesi',
    responsible: 'Sorumlu Rol',
    due: 'Son Tarih',
    evidence: 'Kanıt',
    verifier: 'Doğrulayan',
    criteria: 'Tamamlanma Ölçütü',
    status: 'Durum',
    add: 'Ek Önlem Ekle',
    remove: '{{n}}. önlemi kaldır',
    none: 'Ek önlem yok.',
  },
  effects: { likelihood: 'Olasılık', severity: 'Şiddet', both: 'İkisi' },
  additionalStatuses: {
    draft: 'Taslak (öneri)',
    planned: 'Planlandı',
    in_progress: 'Sürüyor',
    implemented: 'Uygulandı',
    verified: 'Doğrulandı',
  },
  residual: {
    note: 'Ek önlemlerden sonraki hedef kalan risk. Kabul durumu şirketin risk matrisinden gelir.',
    likelihood: 'Olasılık',
    severity: 'Şiddet',
    notSet: 'Girilmedi',
    rationale: 'Gerekçe',
    dependency: 'Bağımlılık',
    scores: 'İlk · Güncel · Hedef',
    acceptance: 'Kabul',
    estimate: 'Doğrulanana dek tahmin',
    verified: 'Doğrulandı',
    acceptedBy: 'Kabul eden: {{roles}}',
  },
  publication: {
    workflow: 'İş Akışı',
    issues: 'Göndermeden önce',
    issuesNone: 'Hazır: içerik yayım denetimlerinden geçiyor.',
    acceptors: 'Yükseltme kabulü',
    acceptorsNone: 'Hiçbir satır ofiste yükseltme kabulü gerektirmiyor.',
    acceptorsVessel: 'Gemide {{roles}} operasyondan önce kabul eder (doğrulama).',
    accepted: 'Kabul edildi',
    pending: 'Bekliyor',
    history: 'Geçmiş',
    historyNone: 'Henüz iş akışı adımı yok.',
    sameActor: 'gönderenle aynı kişi',
    versions: 'Yayımlı Sürümler',
    versionsNone: 'Henüz yayımlı sürüm yok.',
    version: 'Revizyon {{revision}} · {{at}} · {{by}}',
    migratedVersion: 'Revizyon {{revision}} · şablondan dönüştü · {{at}}',
    matrix: 'Risk matrisi v{{version}}',
    legacy: 'Legacy 5-band',
    assignments: 'Atanan Gemiler',
    assignmentsNote: 'Gemi son yayımlı sürümü yalnız gemi tipiyle ya da adıyla atanmışsa kullanır.',
    assignmentsNone: 'Atanmamış: hiçbir gemi güncel olarak kullanamaz.',
    assignGroup: 'Gemi tipi',
    assignVessel: 'Gemi',
    assign: 'Ata',
    unassign: '{{target}} atamasını kaldır',
    groupLabel: 'Bütün {{group}}',
  },
  events: {
    submitted: 'Gönderildi',
    review_started: 'İnceleme başladı',
    returned: 'İade edildi',
    accepted: 'Yükseltme kabul edildi ({{role}})',
    approved: 'Onaylandı',
    published: 'Yayımlandı',
  },
  issues: {
    ROWS_REQUIRED: 'En az bir satır ekleyin.',
    PREPARED_DAY_REQUIRED: 'Hazırlama tarihini girin (Genel).',
    REVIEW_DAY_REQUIRED: 'Gözden geçirme tarihini girin (Genel).',
    REVIEW_BEFORE_PREPARED: 'Gözden geçirme tarihi hazırlamadan sonra olmalı.',
    REVIEW_TOO_LATE: 'Gözden geçirme en geç hazırlamadan 12 ay sonradır.',
    HAZARD_REQUESTED: 'Satır {{n}}: istenen tehlikeyi ana veriden bağlayın.',
    CURRENT_ABOVE_INITIAL: 'Satır {{n}}: güncel risk ilk riskten yüksek olamaz.',
    CURRENT_WITHOUT_CONTROLS:
      'Satır {{n}}: güncel risk yalnız yerinde doğrulanmış mevcut önlemle düşer.',
    ADDITIONAL_CONTROLS_REQUIRED:
      'Satır {{n}}: risk kabul eşiğinin üstünde; taslak olmayan bir ek önlem ekleyin.',
    ADDITIONAL_CONTROL_INCOMPLETE: 'Satır {{n}}: her ek önleme beklenen etki ve sorumlu rol verin.',
    RESIDUAL_REQUIRED: 'Satır {{n}}: hedef kalan riski girin.',
    RESIDUAL_ABOVE_INITIAL: 'Satır {{n}}: kalan risk ilk riskten yüksek olamaz.',
    RESIDUAL_REDUCTION_UNSUPPORTED:
      'Satır {{n}}: taslak önlem riski düşürmez; önce ek önlemi planlayın.',
    SEVERITY_REDUCTION_UNSUPPORTED:
      'Satır {{n}}: şiddeti yalnız şiddeti düşürmesi beklenen, KKD dışı bir önlem düşürür.',
    RESIDUAL_RATIONALE_REQUIRED: 'Satır {{n}}: kalan riskin neden düşük olduğunu yazın.',
    RESIDUAL_UNACCEPTABLE: 'Satır {{n}}: kalan risk kabul edilemez; yayımlanamaz ve kullanılamaz.',
  },
  errors: {
    GENERIC: 'İşlem yapılamadı.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    NOT_FOUND: 'Değerlendirme bulunamadı.',
    VALIDATION_FAILED: 'Bazı alanlar geçerli değil.',
    INVALID_RA_LIBRARY: 'Bazı alanlar geçerli değil: {{detail}}',
    ROW_VERSION_CONFLICT: 'Değerlendirmeyi başkası değiştirdi. Yenileyip yeniden deneyin.',
    CONTENT_HASH_MISMATCH: 'Değerlendirme bu arada değişti. Yenileyip yeniden deneyin.',
    REVISION_LOCKED: 'Revizyon incelemede; değiştirilemez.',
    INVALID_TRANSITION: 'Bu adım bugünkü durumda yapılamaz.',
    STALE_DECISION: 'Değerlendirme bu arada değişti. Yenileyip yeniden deneyin.',
    REASON_REQUIRED: 'En az 5 karakterlik bir gerekçe yazın.',
    ACCEPTANCE_REQUIRED: 'Önce yükseltilen risk kabul edilmeli ({{detail}}).',
    ACCEPTANCE_NOT_REQUIRED: 'Bu rolden yükseltme kabulü gerekmiyor.',
    ACCEPTOR_ROLE_REQUIRED: 'Yalnız şu rolü taşıyan kabul edebilir: {{detail}}.',
    ALREADY_ACCEPTED: 'Bu rol için risk zaten kabul edildi.',
    NOT_APPROVED: 'Önce revizyon onaylanmalı.',
    MATRIX_VERSION_MISMATCH: 'Risk matrisi değişti. Yenileyip yeniden onaylayın.',
    TEMPLATE_NOT_APPROVED:
      '{{detail}} faaliyeti kıyı teknik incelemesinden geçmedi: henüz yayımlanamaz ve gemiye atanamaz (Ana Veri ▸ Faaliyetler).',
    LINK_NOT_ASSESSED:
      'Faaliyetin bir risk satırında başlangıç olasılık ve şiddeti henüz yok ({{detail}}). Önce Ana Veri’de değerlendirin.',
    DUPLICATE_CODE: 'Faaliyetin zaten risk değerlendirmesi var.',
    DUPLICATE_ASSIGNMENT: 'Değerlendirme oraya zaten atanmış.',
    NOT_PUBLISHED: 'Önce değerlendirmeyi yayımlayın.',
    ACTIVITY_RETIRED: 'Faaliyet pasif.',
    REAUTHENTICATION_REQUIRED: 'Onaylamak için parolanızı girin.',
    INVALID_CREDENTIALS: 'Parola yanlış.',
  },
};
